import { ViewBooking } from "collins-js-sdk";
import React, { useMemo } from "react";
import { EDSBadge } from "../../../../../../../components/eds/EDSBadge";
import { useDiaryContext } from "../../../../../../../context/DiaryContext";
import { BookingLabelsEnum } from "../../../../../../../types/bookingEnums";
import { getBookingTypeById } from "../../../../../../../utils/singleBooking";
import { WebViewTooltip } from "../../../../SubHeader/WebViewTooltip";

interface DiaryBookingCardBookingTypeBadgeProps {
  booking: ViewBooking;
  isOnRunSheet?: boolean;
}

export const DiaryBookingCardBookingTypeBadge =
  React.memo<DiaryBookingCardBookingTypeBadgeProps>(
    ({ booking, isOnRunSheet }) => {
      const { venueConfig } = useDiaryContext();

      const bookingType = useMemo(() => {
        return getBookingTypeById({
          bookingTypeId: booking.type?.id,
          venueConfig
        });
      }, [booking.type?.id, venueConfig]);

      // Calculate badge max width based on feature icon count
      const badgeMaxWidth = useMemo(() => {
        // Count feature icons (excluding guest count)
        const featureIconsCount = [
          (booking.labels?.length ?? 0) > 0,
          (booking.linkedBookings?.length ?? 0) > 0 ||
            booking.labels?.find(
              (label: string) => label === BookingLabelsEnum.LinkedBooking
            ),
          booking.company?.[0],
          booking.doubleConfirmed,
          (booking.comments?.length ?? 0) > 0,
          booking.hasPreorders,
          (booking.deposits?.length ?? 0) > 0,
          booking.notes ?? booking.offer?.id,
          booking.labels?.find((label: string) => label === "VIP")
        ].filter(Boolean).length;

        // If there are 3+ feature icons, constrain badge width
        if (featureIconsCount >= 3) {
          if (!bookingType?.name) {
            return "90px"; // Default when no booking type and many icons
          }

          const bookingTypeLength = bookingType.name.length;
          // Dynamic badge width based on booking type length when many icons
          if (bookingTypeLength <= 5) {
            return "70px"; // Short booking types - smaller badge, more space for icons
          } else if (bookingTypeLength <= 10) {
            return "80px"; // Medium booking types - moderate badge width
          } else {
            return "90px"; // Longer booking types - constrained badge width
          }
        } else {
          // Few icons (0-2), allow larger badge width
          if (!bookingType?.name) {
            return "140px"; // Default when no booking type and few icons
          }

          const bookingTypeLength = bookingType.name.length;
          // Allow larger badge width when few icons
          if (bookingTypeLength <= 5) {
            return "120px"; // Short booking types
          } else if (bookingTypeLength <= 10) {
            return "130px"; // Medium booking types
          } else {
            return "140px"; // Longer booking types - maximum badge width
          }
        }
      }, [
        bookingType?.name,
        booking.labels,
        booking.linkedBookings,
        booking.company,
        booking.doubleConfirmed,
        booking.comments,
        booking.hasPreorders,
        booking.deposits,
        booking.notes,
        booking.offer
      ]);

      if (!bookingType || isOnRunSheet) {
        return null;
      }

      const bookingTypeColor = bookingType?.color;
      const badgeElement = (
        <EDSBadge
          className="DiarySidebarListItem__detailsType"
          variant="default"
          style={
            {
              "--booking-type-color": bookingTypeColor ?? "hsl(var(--primary))",
              "--booking-type-foreground": bookingTypeColor
                ? "white"
                : "hsl(var(--primary-foreground))",
              maxWidth: badgeMaxWidth
            } as React.CSSProperties
          }
        >
          <span
            className="DiarySidebarListItem_detailsTypeText"
            style={{ maxWidth: badgeMaxWidth }}
          >
            {bookingType.name}
          </span>
        </EDSBadge>
      );

      return (
        <WebViewTooltip
          content={bookingType.name}
          side="bottom"
          showOnlyWhenTruncated={true}
        >
          {badgeElement}
        </WebViewTooltip>
      );
    }
  );

DiaryBookingCardBookingTypeBadge.displayName =
  "DiaryBookingCardBookingTypeBadge";
